import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, MapPin, Sparkles, Users } from "lucide-react";
import { api, mediaUrl, money, typeLabel } from "../api/client";
import VenueCard from "../components/VenueCard";

const emptyForm = {
  guestName: "",
  guestEmail: "",
  guestPhone: "",
  type: "stay",
  checkIn: "",
  checkOut: "",
  eventDate: "",
  guests: 2,
  itemId: "",
  notes: "",
};

export default function VenuePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setError("");
    setStatus("");
    setForm(emptyForm);
    api(`/api/venues/${slug}`)
      .then(setData)
      .catch((err) => setError(err.message));
  }, [slug]);

  const venue = data?.venue;
  const items = useMemo(() => {
    if (!venue) return [];
    return form.type === "event" ? venue.packages || [] : venue.rooms || [];
  }, [venue, form.type]);

  async function book(event) {
    event.preventDefault();
    setSending(true);
    setStatus("");
    try {
      await api(`/api/venues/${slug}/reservations`, {
        method: "POST",
        body: JSON.stringify(form),
      });
      setStatus("Request received. The house will confirm shortly.");
      setForm(emptyForm);
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSending(false);
    }
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-4xl">This page is dark</h1>
        <p className="mt-3 text-ink/60">{error}</p>
        <Link to="/venues" className="btn-gold mt-6">
          Back to venues
        </Link>
      </div>
    );
  }

  if (!venue) {
    return <div className="py-24 text-center text-ink/50">Dressing the room…</div>;
  }

  return (
    <div>
      <div className="relative h-[52vh] min-h-[320px]">
        {venue.coverImage ? (
          <img
            src={mediaUrl(venue.coverImage)}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-blush" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
        <div className="absolute bottom-8 left-0 right-0 mx-auto max-w-7xl px-4 text-cream sm:px-6 lg:px-8">
          <p className="chip bg-gold text-ink">{typeLabel(venue.type)}</p>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl">{venue.name}</h1>
          <p className="mt-2 flex flex-wrap items-center gap-4 text-cream/80">
            <span className="inline-flex items-center gap-1">
              <MapPin size={16} /> {venue.address}, {venue.city}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users size={16} /> {venue.capacity} guests
            </span>
            <span>★ {venue.rating}</span>
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.4fr_0.8fr] lg:px-8">
        <div>
          <p className="text-xl italic text-rose">{venue.tagline}</p>
          <p className="mt-4 leading-relaxed text-ink/75">{venue.description}</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {(venue.amenities || []).map((item) => (
              <span key={item} className="chip bg-white text-ink/70">
                {item}
              </span>
            ))}
          </div>

          {!!venue.gallery?.length && (
            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {venue.gallery.map((url) => (
                <img
                  key={url}
                  src={mediaUrl(url)}
                  alt=""
                  className="h-52 w-full rounded-[1.6rem] object-cover"
                />
              ))}
            </div>
          )}

          {!!venue.rooms?.length && (
            <section className="mt-12">
              <h2 className="font-serif text-3xl">Stay</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {venue.rooms.map((room) => (
                  <article key={room.id} className="card overflow-hidden">
                    {room.image && (
                      <img
                        src={mediaUrl(room.image)}
                        alt=""
                        className="h-36 w-full object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-serif text-xl">{room.name}</h3>
                      <p className="mt-1 text-sm text-ink/55">
                        Sleeps {room.capacity} · {room.count} keys
                      </p>
                      <p className="mt-2 font-semibold text-rose">
                        {money(room.price, venue.currency)} / night
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {!!venue.packages?.length && (
            <section className="mt-12">
              <h2 className="font-serif text-3xl">Celebrate</h2>
              <div className="mt-5 space-y-4">
                {venue.packages.map((pack) => (
                  <article key={pack.id} className="card p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-serif text-2xl">{pack.name}</h3>
                        <p className="text-sm text-ink/55">Up to {pack.guests} guests</p>
                      </div>
                      <p className="font-semibold text-rose">
                        {money(pack.price, venue.currency)}
                      </p>
                    </div>
                    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(pack.includes || []).map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-ink/70">
                          <Check size={14} className="text-teal" /> {item}
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 h-max">
          <form onSubmit={book} className="card p-6">
            <p className="flex items-center gap-2 text-sm font-semibold text-plum">
              <Sparkles size={16} /> Reserve with {venue.name}
            </p>
            <p className="mt-1 text-sm text-ink/55">
              From {money(venue.startingPrice, venue.currency)}
            </p>
            <div className="mt-5 space-y-3">
              <div>
                <label className="label">Your name</label>
                <input
                  className="field"
                  required
                  value={form.guestName}
                  onChange={(e) => setForm({ ...form, guestName: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  className="field"
                  required
                  value={form.guestEmail}
                  onChange={(e) => setForm({ ...form, guestEmail: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Phone</label>
                <input
                  className="field"
                  required
                  value={form.guestPhone}
                  onChange={(e) => setForm({ ...form, guestPhone: e.target.value })}
                />
              </div>
              <div>
                <label className="label">I want to</label>
                <select
                  className="field"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value, itemId: "" })}
                >
                  <option value="stay">Stay overnight</option>
                  <option value="event">Host a wedding / party</option>
                </select>
              </div>
              {form.type === "stay" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Check-in</label>
                    <input
                      type="date"
                      className="field"
                      required
                      value={form.checkIn}
                      onChange={(e) => setForm({ ...form, checkIn: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Check-out</label>
                    <input
                      type="date"
                      className="field"
                      required
                      value={form.checkOut}
                      onChange={(e) => setForm({ ...form, checkOut: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label">Event date</label>
                  <input
                    type="date"
                    className="field"
                    required
                    value={form.eventDate}
                    onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                  />
                </div>
              )}
              <div>
                <label className="label">{form.type === "stay" ? "Room" : "Package"}</label>
                <select
                  className="field"
                  value={form.itemId}
                  onChange={(e) => setForm({ ...form, itemId: e.target.value })}
                >
                  <option value="">Select…</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Guests</label>
                <input
                  type="number"
                  min="1"
                  className="field"
                  value={form.guests}
                  onChange={(e) => setForm({ ...form, guests: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea
                  className="field min-h-[90px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
            {status && <p className="mt-3 text-sm text-plum">{status}</p>}
            <button className="btn-rose mt-5 w-full" disabled={sending}>
              {sending ? "Sending…" : "Request reservation"}
            </button>
          </form>
        </aside>
      </div>

      {!!data.related?.length && (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <h2 className="mb-6 font-serif text-3xl">Other houses</h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {data.related.map((item) => (
              <VenueCard key={item.id} venue={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
